import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {MyListHeaderEntry} from 'components/MyListHeaderEntry'
import {PreferencesWidgetWithStore} from 'components/PreferencesWidgetWithStore'
import {SearchFormDesktop} from 'components/SearchBox/Home/SearchFormDesktop'
import UserWidget from 'components/UserWidget'
import {HeaderChatButton} from 'components/VioMcpChat/HeaderChatButton'
import {useHowItWorks} from 'hooks/useHowItWorks'
import {getLanguageCode} from 'modules/meta/selectors'
import {getHomeUrlWithUTMParams} from 'utils/utmParams'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {NavBar} from '@vio/atlas/NavBar'
import {useBodyScrollLockObserver} from '@vio/atlas/Overlay/useBodyScrollLock'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {getLocalizedContactUrl} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {NavBarBrandLogo} from '@vio/shared/src/navBar'
import {useIsMyListVisibilityOn} from '@vio/shared/src/priceWatch/hooks/useIsMyListVisibilityOn'
import {MyListLinkComponent} from '@vio/shared/src/priceWatch/hooks/useTrackMyListLinkClick'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'

const HeaderWrapper = styled.div<{
  scrollBarGap: number
}>`
  padding-right: ${({scrollBarGap}) => scrollBarGap}px;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1;
  &.--has-shadow {
    box-shadow: ${cssTheme.shadows.fixed};
  }
`

const SearchFormDesktopWrapper = styled.div`
  margin: 0 auto;
  flex-grow: 1;
  ${linariaMq.desktopMd} {
    flex-grow: 0;
    overflow: hidden;
    padding: 0 ${cssTheme.layout.spacing.s250};
    width: fit-content;
  }
`

export const BaseHomeDesktopHeader = ({
  withSearchForm = false,
  showSupportButton = true,
  isStatic = true
}) => {
  const isHydrated = useIsHydrated()
  const {brand} = useBrand()
  const {auth} = useBrandCapabilities()
  const logoURL = isHydrated ? getHomeUrlWithUTMParams() : '/'

  const {scrollBarGap} = useBodyScrollLockObserver()
  const {isDesktopXs, isDesktopSm} = useDeviceLayout()
  const languageCode = useSelector(getLanguageCode)
  const {isEnabled: isHowItWorksEnabled, link: howItWorksLink} = useHowItWorks()

  const isTablet = isDesktopXs || isDesktopSm
  const hasUserWidget = auth.showUserWidget
  const isMyListVisibilityOn = useIsMyListVisibilityOn()

  const logo = (
    <NavBarBrandLogo
      brand={brand}
      logoUrl={logoURL}
      logoAlt={brand.name}
      BrandLogoComponent={BrandLogo}
    />
  )

  const centerContent = withSearchForm && (
    <SearchFormDesktopWrapper>
      <SearchFormDesktop />
    </SearchFormDesktopWrapper>
  )

  const handleNavigateSupportPage = () =>
    window.open(getLocalizedContactUrl(brand, languageCode), '_blank')

  return (
    <HeaderWrapper
      className={cx(!isStatic && '--has-shadow')}
      scrollBarGap={scrollBarGap}
    >
      <NavBar
        wrapCenterContent={isTablet}
        startContent={logo}
        variant={isStatic ? 'transparent' : 'default'}
        centerContent={centerContent}
        endContent={
          <Box display="flex" gap="s300">
            {!isMyListVisibilityOn && <PreferencesWidgetWithStore />}
            {isHowItWorksEnabled && (
              <Button
                href={howItWorksLink}
                target="_blank"
                variant="ghost"
                size="md"
              >
                <FormattedMessageWrapper
                  {...brand.messages.howItWorksMessage}
                  values={{brandName: brand.name}}
                />
              </Button>
            )}
            {showSupportButton && (
              <Button
                onClick={handleNavigateSupportPage}
                variant="ghost"
                size="md"
              >
                <FormattedMessageWrapper
                  id="support247"
                  defaultMessage="Support 24/7"
                />
              </Button>
            )}
            <HeaderChatButton />
            <MyListHeaderEntry
              component={MyListLinkComponent.HomeDesktopHeader}
            />
            {isMyListVisibilityOn && <PreferencesWidgetWithStore />}
            {hasUserWidget && <UserWidget buttonVariant="ghost" />}
          </Box>
        }
      />
    </HeaderWrapper>
  )
}
